import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
  Divider,
  FormControl,
  InputLabel,
  Select,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as ProcessIcon,
  Payment as PaidIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  People as PeopleIcon,
  AccountBalance as AccountBalanceIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import PayrollService from '../../services/payroll.service';

const PayrollList: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [payrolls, setPayrolls] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState<string>('period');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedMonth, setSelectedMonth] = useState<string>(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });

  useEffect(() => {
    fetchPayrolls();
  }, [statusFilter, selectedMonth, sortField, sortOrder]);

  const fetchPayrolls = async () => {
    try {
      setLoading(true);
      const params: any = {
        populate: ['employee', 'processedBy'],
        'pagination[pageSize]': 100,
        sort: `${sortField}:${sortOrder}`,
      };

      if (statusFilter !== 'all') {
        params['filters[status][$eq]'] = statusFilter;
      }

      if (selectedMonth) {
        params['filters[period][$eq]'] = selectedMonth;
      }

      const response = await PayrollService.getAll(params);
      setPayrolls(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching payrolls:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate month options for the last 12 months
  const generateMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const value = `${year}-${month}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label });
    }
    return options;
  };

  // Format period (YYYY-MM) to readable format (e.g., "January 2024")
  const formatPeriod = (period: string) => {
    if (!period) return '-';
    try {
      const [year, month] = period.split('-');
      const date = new Date(parseInt(year), parseInt(month) - 1, 1);
      return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    } catch {
      return period;
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this payroll record?')) {
      try {
        await PayrollService.delete(id);
        fetchPayrolls();
      } catch (error) {
        console.error('Error deleting payroll:', error);
      }
    }
  };

  const handleProcess = async (id: number) => {
    if (window.confirm('Mark this payroll as processed?')) {
      try {
        await PayrollService.processPay(id);
        fetchPayrolls();
      } catch (error) {
        console.error('Error processing payroll:', error);
      }
    }
  };

  const handleMarkAsPaid = async (id: number) => {
    if (window.confirm('Mark this payroll as paid?')) {
      try {
        const today = new Date().toISOString().split('T')[0];
        await PayrollService.markAsPaid(id, today);
        fetchPayrolls();
      } catch (error) {
        console.error('Error marking payroll as paid:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'processed':
        return 'info';
      case 'draft':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatCurrency = (amount: number, currency: string = 'OMR') => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'OMR',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const filteredPayrolls = payrolls.filter((payroll) => {
    const employee = payroll.attributes?.employee?.data?.attributes;
    const searchLower = searchTerm.toLowerCase();
    
    const matchesSearch =
      !searchTerm ||
      employee?.firstName?.toLowerCase().includes(searchLower) ||
      employee?.lastName?.toLowerCase().includes(searchLower) ||
      employee?.employeeNumber?.toLowerCase().includes(searchLower) ||
      formatPeriod(payroll.attributes?.period || '').toLowerCase().includes(searchLower);

    return matchesSearch;
  });

  // Calculate summary statistics
  const summaryStats = {
    totalEmployees: new Set(filteredPayrolls.map(p => p.attributes?.employee?.data?.id)).size,
    totalPayroll: filteredPayrolls.reduce((sum, p) => sum + (p.attributes?.netSalary || 0), 0),
    totalBaseSalary: filteredPayrolls.reduce((sum, p) => sum + (p.attributes?.baseSalary || 0), 0),
    totalBonuses: filteredPayrolls.reduce((sum, p) => sum + (p.attributes?.bonuses || 0), 0),
    totalDeductions: filteredPayrolls.reduce((sum, p) => sum + (p.attributes?.deductions || 0), 0),
    paidCount: filteredPayrolls.filter(p => p.attributes?.status === 'paid').length,
    processedCount: filteredPayrolls.filter(p => p.attributes?.status === 'processed').length,
    draftCount: filteredPayrolls.filter(p => p.attributes?.status === 'draft').length,
  };

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Payroll Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage employee payroll records, process payments, and track payroll status
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<AddIcon />}
          onClick={() => navigate('/payroll/create')}
          sx={{ minWidth: 150 }}
        >
          Create Payroll
        </Button>
      </Stack>

      {/* Summary Cards */}
      {!loading && filteredPayrolls.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' }, minWidth: { xs: '100%', sm: '200px' } }}>
            <Card elevation={2}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <PeopleIcon color="primary" />
                  <Typography variant="body2" color="text.secondary">
                    Employees
                  </Typography>
                </Stack>
                <Typography variant="h5" fontWeight="bold">
                  {summaryStats.totalEmployees}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  in {formatPeriod(selectedMonth)}
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' }, minWidth: { xs: '100%', sm: '200px' } }}>
            <Card elevation={2}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <AccountBalanceIcon color="success" />
                  <Typography variant="body2" color="text.secondary">
                    Total Payroll
                  </Typography>
                </Stack>
                <Typography variant="h5" fontWeight="bold" color="success.main">
                  {formatCurrency(summaryStats.totalPayroll)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Net salary amount
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' }, minWidth: { xs: '100%', sm: '200px' } }}>
            <Card elevation={2}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <TrendingUpIcon color="info" />
                  <Typography variant="body2" color="text.secondary">
                    Bonuses
                  </Typography>
                </Stack>
                <Typography variant="h5" fontWeight="bold" color="info.main">
                  {formatCurrency(summaryStats.totalBonuses)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Total bonuses paid
                </Typography>
              </CardContent>
            </Card>
          </Box>
          <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 8px)', md: '1 1 calc(25% - 12px)' }, minWidth: { xs: '100%', sm: '200px' } }}>
            <Card elevation={2}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                  <PaidIcon color="warning" />
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                  <Chip label={`${summaryStats.paidCount} Paid`} color="success" size="small" />
                  <Chip label={`${summaryStats.processedCount} Processed`} color="info" size="small" />
                  <Chip label={`${summaryStats.draftCount} Draft`} color="warning" size="small" />
                </Stack>
              </CardContent>
            </Card>
          </Box>
        </Box>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2.5, mb: 2 }} elevation={1}>
        <Typography variant="subtitle2" fontWeight="bold" mb={2} color="text.secondary">
          FILTERS & SEARCH
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2}>
          <TextField
            fullWidth
            label="Search Employees"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by employee name, number, or period"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            select
            label="Select Month & Year"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            sx={{ minWidth: 220 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarIcon color="action" />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">
              <em>All Periods</em>
            </MenuItem>
            {generateMonthOptions().map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label="Payment Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="draft">Draft</MenuItem>
            <MenuItem value="processed">Processed</MenuItem>
            <MenuItem value="paid">Paid</MenuItem>
            <MenuItem value="cancelled">Cancelled</MenuItem>
          </TextField>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
              value={`${sortField}:${sortOrder}`}
              label="Sort By"
              onChange={(e) => {
                const [field, order] = e.target.value.split(':');
                setSortField(field);
                setSortOrder(order as 'asc' | 'desc');
              }}
            >
              <MenuItem value="period:desc">Period (Newest First)</MenuItem>
              <MenuItem value="period:asc">Period (Oldest First)</MenuItem>
              <MenuItem value="netSalary:desc">Net Salary (High to Low)</MenuItem>
              <MenuItem value="netSalary:asc">Net Salary (Low to High)</MenuItem>
              <MenuItem value="baseSalary:desc">Base Salary (High to Low)</MenuItem>
              <MenuItem value="baseSalary:asc">Base Salary (Low to High)</MenuItem>
              <MenuItem value="status:asc">Status (A-Z)</MenuItem>
              <MenuItem value="status:desc">Status (Z-A)</MenuItem>
              <MenuItem value="paymentDate:desc">Payment Date (Newest First)</MenuItem>
              <MenuItem value="paymentDate:asc">Payment Date (Oldest First)</MenuItem>
              <MenuItem value="createdAt:desc">Created (Newest First)</MenuItem>
              <MenuItem value="createdAt:asc">Created (Oldest First)</MenuItem>
            </Select>
          </FormControl>
          {(searchTerm || statusFilter !== 'all' || selectedMonth) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setSelectedMonth('');
              }}
              sx={{ minWidth: 120 }}
            >
              Clear Filters
            </Button>
          )}
        </Stack>
        {selectedMonth && (
          <Box mt={2}>
            <Chip
              label={`Showing payroll for: ${formatPeriod(selectedMonth)}`}
              color="primary"
              variant="outlined"
              onDelete={() => setSelectedMonth('')}
            />
          </Box>
        )}
      </Paper>

      <TableContainer component={Paper} elevation={1}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'grey.50' }}>
              <TableCell sx={{ fontWeight: 'bold' }}>Pay Period</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Employee Information</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Base Salary</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Bonuses</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Deductions</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Net Salary</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Payment Date</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Loading payroll data...</Typography>
                </TableCell>
              </TableRow>
            ) : filteredPayrolls.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary" variant="body1">
                    No payroll records found
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={1}>
                    {selectedMonth
                      ? `Try selecting a different month or clearing the filters`
                      : `Create a new payroll record to get started`}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredPayrolls.map((payroll) => {
                const attrs = payroll.attributes;
                const employee = attrs?.employee?.data?.attributes;
                const status = attrs?.status || 'draft';
                const isDraft = status === 'draft';
                const isProcessed = status === 'processed';

                return (
                  <TableRow 
                    key={payroll.id}
                    sx={{ 
                      '&:hover': { backgroundColor: 'action.hover' },
                      borderLeft: status === 'paid' ? '3px solid' : 'none',
                      borderLeftColor: status === 'paid' ? 'success.main' : 'transparent',
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {formatPeriod(attrs?.period)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {attrs?.period || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {employee
                          ? `${employee.firstName} ${employee.lastName}`
                          : 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {employee?.employeeNumber || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(attrs?.baseSalary, attrs?.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={attrs?.bonuses > 0 ? 'success.main' : 'text.secondary'}>
                        {formatCurrency(attrs?.bonuses, attrs?.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={attrs?.deductions > 0 ? 'error.main' : 'text.secondary'}>
                        {formatCurrency(attrs?.deductions, attrs?.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body1" fontWeight="bold" color="primary.main">
                        {formatCurrency(attrs?.netSalary, attrs?.currency)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={status.charAt(0).toUpperCase() + status.slice(1)}
                        color={getStatusColor(status)}
                        size="small"
                        sx={{ fontWeight: 'medium' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {attrs?.paymentDate ? formatDate(attrs.paymentDate) : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {isDraft && (
                          <Tooltip title="Mark as Processed">
                            <IconButton
                              size="small"
                              color="info"
                              onClick={() => handleProcess(payroll.id)}
                            >
                              <ProcessIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isProcessed && (
                          <Tooltip title="Mark as Paid">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleMarkAsPaid(payroll.id)}
                            >
                              <PaidIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit Payroll">
                          <IconButton
                            size="small"
                            onClick={() => navigate(`/payroll/${payroll.id}/edit`)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Payroll">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(payroll.id)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Detailed Summary */}
      {!loading && filteredPayrolls.length > 0 && (
        <Paper sx={{ p: 3, mt: 3 }} elevation={1}>
          <Typography variant="subtitle1" fontWeight="bold" mb={2}>
            Payroll Summary for {formatPeriod(selectedMonth) || 'All Periods'}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '200px' } }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Records
              </Typography>
              <Typography variant="h6" fontWeight="bold">
                {filteredPayrolls.length}
              </Typography>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '200px' } }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Base Salary
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="text.primary">
                {formatCurrency(summaryStats.totalBaseSalary)}
              </Typography>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '200px' } }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Bonuses
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="success.main">
                {formatCurrency(summaryStats.totalBonuses)}
              </Typography>
            </Box>
            <Box sx={{ flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 12px)', md: '1 1 calc(25% - 18px)' }, minWidth: { xs: '100%', sm: '200px' } }}>
              <Typography variant="caption" color="text.secondary" display="block">
                Total Deductions
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="error.main">
                {formatCurrency(summaryStats.totalDeductions)}
              </Typography>
            </Box>
            <Box sx={{ width: '100%' }}>
              <Divider sx={{ my: 1 }} />
            </Box>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ 
                backgroundColor: 'primary.light', 
                p: 2, 
                borderRadius: 1,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography variant="h6" fontWeight="bold" color="primary.contrastText">
                  Total Net Payroll Amount
                </Typography>
                <Typography variant="h5" fontWeight="bold" color="primary.contrastText">
                  {formatCurrency(summaryStats.totalPayroll)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default PayrollList;

